import React from 'react';
import { Box, Typography } from '@mui/material';
import ZohoAkMoA4Template from '../templates/ZohoAkMoA4Template';

interface InvoiceItem {
    id: string;
    name: string;
    description?: string;
    hsnCode: string;
    quantity: number;
    rate: number;
    gstRate: number;
    discount: number;
    amount: number;
    taxAmount: number;
    total: number;
    unit?: string;
}

interface InvoiceData {
    type?: 'invoice' | 'quotation';
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    client: {
        name: string;
        address?: string;
        city?: string;
        state?: string;
        pincode?: string;
        gstin?: string;
        email?: string;
        phone?: string;
    } | null;
    items: InvoiceItem[];
    notes?: string;
    terms?: string;
}

interface BusinessDetails {
    name: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    gstin?: string;
    email?: string;
    phone?: string;
    logoUrl?: string;
    bankDetails?: {
        bankName?: string;
        accountNumber?: string;
        ifsc?: string;
        upiId?: string;
        branch?: string;
    };
    primaryColor?: string;
}

interface InvoiceLivePreviewProps {
    invoiceData: InvoiceData;
    businessDetails: BusinessDetails;
}

const InvoiceLivePreview: React.FC<InvoiceLivePreviewProps> = ({
    invoiceData,
    businessDetails,
}) => {
    // Map items to ZohoAkMoA4Template format
    const templateItems = invoiceData.items.map(item => ({
        productName: item.name || '',
        description: item.description || '',
        quantity: Number(item.quantity) || 1,
        unit: item.unit || 'Nos',
        hsn: item.hsnCode || '',
        gstRate: Number(item.gstRate) || 0,
        taxableValue: Number(item.amount) || 0,
        taxAmount: Number(item.taxAmount) || 0,
        total: Number(item.total) || Number(item.amount) + Number(item.taxAmount),
    }));

    // Map business details to template format
    const templateBusiness = {
        name: businessDetails.name || '',
        address: businessDetails.address || '',
        city: businessDetails.city || '',
        state: businessDetails.state || '',
        pincode: businessDetails.pincode || '',
        phone: businessDetails.phone || '',
        email: businessDetails.email || '',
        gstin: businessDetails.gstin || '',
        bankName: businessDetails.bankDetails?.bankName || '',
        bankBranch: businessDetails.bankDetails?.branch || '',
        accountNumber: businessDetails.bankDetails?.accountNumber || '',
        ifsc: businessDetails.bankDetails?.ifsc || '',
        upiId: businessDetails.bankDetails?.upiId || '',
        logoUrl: businessDetails.logoUrl || '',
        gstRegistered: businessDetails.gstin ? 'registered' : 'not_registered',
        gstType: 'regular',
    };

    // Map client details to template format
    const templateClient = {
        name: invoiceData.client?.name || 'No client selected',
        address: invoiceData.client?.address || '',
        city: invoiceData.client?.city || '',
        state: invoiceData.client?.state || '',
        pincode: invoiceData.client?.pincode || '',
        phone: invoiceData.client?.phone || '',
        email: invoiceData.client?.email || '',
        gstin: invoiceData.client?.gstin || '',
    };

    // Map invoice details
    const templateInvoice = {
        invoiceNumber: invoiceData.invoiceNumber || 'DRAFT',
        invoiceDate: invoiceData.invoiceDate || new Date().toISOString().split('T')[0],
        dueDate: invoiceData.dueDate || '',
        discount: 0,
        termsAndConditions: invoiceData.terms ? invoiceData.terms.split('\n') : [],
    };

    return (
        <Box
            sx={{
                height: '100%',
                overflow: 'auto',
                bgcolor: '#f5f5f5',
                p: 2,
            }}
        >
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Live Preview
            </Typography>

            {/* Scale down to fit the sidebar panel */}
            <Box
                sx={{
                    transformOrigin: 'top left',
                    transform: 'scale(0.52)',
                    width: '192%', // compensate for scale so parent doesn't overflow
                }}
            >
                <ZohoAkMoA4Template
                    businessDetails={templateBusiness}
                    clientDetails={templateClient}
                    invoiceDetails={templateInvoice}
                    items={templateItems}
                    primaryColor={businessDetails.primaryColor}
                />
            </Box>
        </Box>
    );
};

export default InvoiceLivePreview;
